import {
  createContext,
  lazy,
  type ReactNode,
  Suspense,
  useContext,
  useEffect,
  useState,
} from "react";
import type { PaletteHost } from "./palette-host.js";
import { isMac } from "./platform.js";

/**
 * "Jump to": who may open it, and the shortcut that does.
 *
 * Only this file and the trigger are in the app's first load. The palette
 * itself — cmdk, its rows, its search — is its own chunk, fetched when the
 * browser is idle after the shell has drawn, so the first ⌘K is already
 * instant and the first load stays inside its budget (frontend.md).
 */
const CommandPalette = lazy(() =>
  import("./command-palette.js").then((module) => ({ default: module.CommandPalette })),
);

interface PaletteControl {
  readonly open: () => void;
}

const PaletteContext = createContext<PaletteControl | null>(null);

/** Open "Jump to". A no-op outside a signed-in shell, where there is nothing to jump to. */
export function usePalette(): PaletteControl | null {
  return useContext(PaletteContext);
}

function isOpenShortcut(event: KeyboardEvent): boolean {
  return (
    event.key.toLowerCase() === "k" &&
    (isMac() ? event.metaKey : event.ctrlKey) &&
    !event.altKey
  );
}

export function PaletteProvider({
  host,
  children,
}: {
  readonly host: PaletteHost;
  readonly children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  // Mounted once opened, then kept, so a second open keeps its place in memory.
  const [loaded, setLoaded] = useState(false);
  const [control] = useState<PaletteControl>(() => ({
    open: () => {
      setLoaded(true);
      setOpen(true);
    },
  }));

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!isOpenShortcut(event)) return;
      event.preventDefault();
      setLoaded(true);
      setOpen((now) => !now);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  // Fetch the palette's code while nothing else is happening.
  useEffect(() => {
    const warm = () => {
      void import("./command-palette.js");
    };
    if (typeof window.requestIdleCallback === "function") {
      const handle = window.requestIdleCallback(warm, { timeout: 4000 });
      return () => {
        window.cancelIdleCallback(handle);
      };
    }
    const timer = window.setTimeout(warm, 2000);
    return () => {
      window.clearTimeout(timer);
    };
  }, []);

  return (
    <PaletteContext.Provider value={control}>
      {children}
      {loaded ? (
        <Suspense fallback={null}>
          <CommandPalette open={open} onOpenChange={setOpen} host={host} />
        </Suspense>
      ) : null}
    </PaletteContext.Provider>
  );
}
